// src/features/commodities-info/components/CommodityEtfsTable.tsx
//
// Component Island: Top Liquid Proxy ETFs for a commodity. Fetches
// independently of the parent page (own loading skeleton + failure isolation)
// from GET /api/v1/commodities-info/{symbol}/etfs — live FMP batch quotes,
// ranked server-side by volume so the most institutional-grade / liquid ETFs
// surface first. Amber accent matches the Commodities asset class.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchCommodityEtfs, type CommodityETF } from '../api';

const formatCurrency = (val?: number | null): string =>
    val != null
        ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
          }).format(val)
        : 'N/A';

const formatCompact = (val?: number | null): string => {
    if (val == null) return 'N/A';
    if (val >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
    if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
    if (val >= 1e3) return `${(val / 1e3).toFixed(2)}K`;
    return val.toString();
};

export const CommodityEtfsTable = ({ symbol }: { symbol: string }) => {
    const navigate = useNavigate();
    const [etfs, setEtfs] = useState<CommodityETF[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchCommodityEtfs(symbol)
            .then((data) => {
                if (!cancelled) setEtfs(data);
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error(err);
                setError('Unable to load related proxy ETFs.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 flex flex-col h-[350px] shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                Top Proxy ETFs
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-mono font-bold rounded border border-amber-200 dark:border-amber-800/50 uppercase">
                    Live
                </span>
            </h2>

            <div className="relative flex-1 w-full overflow-y-auto">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10">
                        <span className="text-amber-500 font-medium animate-pulse text-sm">
                            Scanning FMP for liquid ETFs…
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        {error}
                    </div>
                )}

                {!isLoading && !error && etfs.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4 text-slate-500 dark:text-slate-400 text-sm">
                        No direct proxy ETFs found for this asset.
                    </div>
                )}

                {!isLoading && !error && etfs.length > 0 && (
                    <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                        <thead className="sticky top-0 border-b border-slate-200 dark:border-slate-700 text-xs uppercase text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 z-10">
                            <tr>
                                <th className="px-2 py-3 font-medium">Symbol</th>
                                <th className="px-2 py-3 font-medium hidden sm:table-cell">
                                    Name
                                </th>
                                <th className="px-2 py-3 font-medium text-right">
                                    Price
                                </th>
                                <th className="px-2 py-3 font-medium text-right">
                                    Change
                                </th>
                                <th className="px-2 py-3 font-medium text-right">
                                    Volume
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {etfs.map((etf) => (
                                <tr
                                    key={etf.symbol}
                                    onClick={() =>
                                        navigate(
                                            `/dashboard/etf-info-page?symbol=${encodeURIComponent(
                                                etf.symbol,
                                            )}`,
                                        )
                                    }
                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors"
                                >
                                    <td className="px-2 py-3 font-mono font-bold text-amber-600 dark:text-amber-400 hover:underline">
                                        {etf.symbol}
                                    </td>
                                    <td
                                        className="px-2 py-3 max-w-[120px] truncate hidden sm:table-cell"
                                        title={etf.name ?? undefined}
                                    >
                                        {etf.name ?? '—'}
                                    </td>
                                    <td className="px-2 py-3 text-right font-mono text-slate-900 dark:text-white">
                                        {formatCurrency(etf.price)}
                                    </td>
                                    <td
                                        className={`px-2 py-3 text-right font-medium ${
                                            (etf.changes_percentage ?? 0) >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}
                                    >
                                        {(etf.changes_percentage ?? 0) >= 0
                                            ? '+'
                                            : ''}
                                        {(etf.changes_percentage ?? 0).toFixed(
                                            2,
                                        )}
                                        %
                                    </td>
                                    <td className="px-2 py-3 text-right font-mono text-slate-500 dark:text-slate-400">
                                        {formatCompact(etf.volume)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default CommodityEtfsTable;
